/**
 * Sky dome mesh using Three.js analytic daylight sky shader.
 *
 * Builds a large sphere with the sky shader applied (BackSide rendering).
 * The sphere encloses the board while Sky.SkyShader handles daylight
 * scattering, horizon glow, and the sun disc.
 */

import * as THREE from 'three';
import {
  getSunDirectionFromSkyConfig,
  resolveDaylightSkyConfig,
  type DaylightSkyConfig,
} from '../atmosphere/daylight-sky';
import type { ShaderRegistry } from '../shaders/types';

export interface SkyDomeConfig extends DaylightSkyConfig {
  readonly radius?: number;
}

export interface SkyDomeResult {
  /** The sky dome mesh. Add to scene groups.environment. */
  readonly mesh: THREE.Mesh;
  /** Normalized direction from board origin toward the visible sun. */
  readonly sunDirection: THREE.Vector3;
  /** Dispose geometry + material. */
  dispose(): void;
}

/**
 * Build a large sphere with the analytic daylight sky shader applied.
 * Sphere: SphereGeometry(100, 32, 16) — large enough to enclose the entire board.
 * Material: sky ShaderMaterial from registry (already has side: BackSide, depthWrite: false).
 * Position: centered at origin. The shader pins depth to the camera far plane.
 */
export function buildSkyDome(
  shaderRegistry: ShaderRegistry,
  config?: SkyDomeConfig,
): SkyDomeResult {
  // ── Geometry ──────────────────────────────────────────────────────────────
  // 32 width segments × 16 height segments ≈ 1024 triangles.
  // Radius 100 encloses the board scene; callers can scale it for wider maps.
  const geometry = new THREE.SphereGeometry(config?.radius ?? 100, 32, 16);

  // ── Material ─────────────────────────────────────────────────────────────
  // Sky shader already sets side: BackSide and depthWrite: false.
  const skyFactory = shaderRegistry.get('sky');
  const material = skyFactory();
  const skyConfig = resolveDaylightSkyConfig(config);
  const sunDirection = getSunDirectionFromSkyConfig(skyConfig);
  const uniforms = material.uniforms;
  if (uniforms['turbidity'] != null) uniforms['turbidity'].value = skyConfig.turbidity;
  if (uniforms['rayleigh'] != null) uniforms['rayleigh'].value = skyConfig.rayleigh;
  if (uniforms['mieCoefficient'] != null) {
    uniforms['mieCoefficient'].value = skyConfig.mieCoefficient;
  }
  if (uniforms['mieDirectionalG'] != null) {
    uniforms['mieDirectionalG'].value = skyConfig.mieDirectionalG;
  }
  if (uniforms['sunPosition']?.value instanceof THREE.Vector3) {
    uniforms['sunPosition'].value.copy(sunDirection);
  }

  // ── Mesh ─────────────────────────────────────────────────────────────────
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = 'daylight-sky-dome';
  mesh.frustumCulled = false;
  // Centered at origin — large enough for board camera movement.
  mesh.position.set(0, 0, 0);

  // ── State ─────────────────────────────────────────────────────────────────
  let disposed = false;

  return {
    mesh,
    sunDirection,

    dispose(): void {
      if (disposed) return;
      disposed = true;
      geometry.dispose();
      material.dispose();
    },
  };
}
